import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, mediaUrl, type ArticleDetail as Article, type ArticleStatus } from '../lib/api';
import { ARTICLE_STATUS, ago, dNum, euro } from '../lib/domain';
import { md } from '../lib/md';
import { useMe, useToast } from '../hooks';
import { CloseButton, Avatar, ErrorBox, Icon, Loading } from '../ui';
import { useBarTitle } from '../barTitle';

export default function ArticleDetail() {
  useBarTitle('Anzeige');
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  const [shown, setShown] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [question, setQuestion] = useState('');
  const {
    data: a,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['article', Number(id)],
    queryFn: () => api<Article>(`/articles/${id}`),
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['article', Number(id)] });
    void qc.invalidateQueries({ queryKey: ['articles'] });
    void qc.invalidateQueries({ queryKey: ['member'] }); // the seller's "Im Flohmarkt" list
  };
  const onErr = (e: Error) => toast(e.message);
  const setStatus = useMutation({
    mutationFn: (status: ArticleStatus) => api(`/articles/${id}/status`, { method: 'PUT', body: { status } }),
    onSuccess: (_d, s) => {
      refresh();
      toast(s === 'active' ? 'Wieder verfügbar' : `Als „${ARTICLE_STATUS[s]}“ markiert`);
    },
    onError: onErr,
  });
  const remove = useMutation({
    mutationFn: () => api(`/articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['articles'] });
      void qc.invalidateQueries({ queryKey: ['member'] });
      toast('Anzeige gelöscht');
      void nav('/flohmarkt', { replace: true });
    },
    onError: onErr,
  });
  const ask = useMutation({
    mutationFn: () => api(`/articles/${id}/questions`, { method: 'POST', body: { body: question } }),
    onSuccess: () => {
      setQuestion('');
      refresh();
    },
    onError: onErr,
  });
  const delQuestion = useMutation({
    mutationFn: (qid: number) => api(`/article-questions/${qid}`, { method: 'DELETE' }),
    onSuccess: refresh,
    onError: onErr,
  });

  if (isLoading) return <Loading />;
  if (error || !a) return <ErrorBox error={error} />;
  const i = Math.min(shown, Math.max(0, a.images.length - 1));
  const img = a.images[i];
  const mineAd = a.seller_id === me?.id;
  // the seller can be asked only while they are an active member (former sellers appear anonymously)
  const sellerActive = !!a.seller?.id && !a.seller.former;
  const sellerFirst = sellerActive ? a.seller!.name.split(' ')[0] : 'Verkäufer';

  return (
    <article className="drawer-page" style={{ ['--dc' as string]: 'var(--navy)' }}>
      <div className="d-head">
        <CloseButton to="/flohmarkt" label="Zurück zum Flohmarkt" />
        <p className="eyebrow">
          Flohmarkt · {a.category}
          {a.condition ? ` · ${a.condition}` : ''}
          {a.size ? ` · Gr. ${a.size}` : ''}
        </p>
        <h1 className="d-title">{a.title}</h1>
        <p className="d-when">
          <span className="price" style={{ fontSize: 30 }}>
            {euro(a.price)}
          </span>
          {a.negotiable && a.price !== null && <span className="muted"> VB</span>}{' '}
          {a.status !== 'active' && (
            <span className={`st ${a.status === 'sold' ? 'st-sold' : 'st-res'}`}>{ARTICLE_STATUS[a.status]}</span>
          )}
        </p>
      </div>
      <div className="d-body">
        {img && (
          <div className="gallery">
            <div className="gallery-main">
              <a href={mediaUrl(img.file)!} target="_blank" rel="noopener" aria-label="Foto in voller Größe öffnen">
                <img src={mediaUrl(img.file)!} alt={a.title} />
              </a>
              {a.images.length > 1 && (
                <>
                  <button
                    className="navbtn prev"
                    onClick={() => setShown((i - 1 + a.images.length) % a.images.length)}
                    aria-label="Vorheriges Foto"
                  >
                    <Icon name="left" sw={2.2} />
                  </button>
                  <button
                    className="navbtn next"
                    onClick={() => setShown((i + 1) % a.images.length)}
                    aria-label="Nächstes Foto"
                  >
                    <Icon name="right" sw={2.2} />
                  </button>
                </>
              )}
            </div>
            {a.images.length > 1 && (
              <div className="gallery-strip">
                {a.images.map((p, k) => (
                  <button key={p.id} onClick={() => setShown(k)} aria-current={k === i} aria-label={`Foto ${k + 1}`}>
                    <img src={mediaUrl(p.thumb)!} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {a.can_edit && (
          <div>
            {!mineAd && (
              <p className="note admin-note">
                <Icon name="shield" sw={1.8} />
                Du kannst diese Anzeige als Admin bearbeiten – aufgegeben hat sie {a.seller?.name ?? 'jemand anderes'}.
              </p>
            )}
            <div className="d-actions">
              <Link className="btn btn-navy btn-sm" to={`/flohmarkt/${a.id}/bearbeiten`}>
                <Icon name="edit" sw={1.8} />
                Bearbeiten
              </Link>
              {(['active', 'reserved', 'sold'] as const)
                .filter(s => s !== a.status)
                .map(s => (
                  <button key={s} className="btn btn-ghost btn-sm" onClick={() => setStatus.mutate(s)}>
                    {s === 'active' ? 'Wieder verfügbar' : ARTICLE_STATUS[s]}
                  </button>
                ))}
              <button className="btn btn-danger btn-sm" onClick={() => setConfirm(true)}>
                <Icon name="trash" sw={1.8} />
                Löschen
              </button>
            </div>
            {confirm && (
              <div className="confirm" role="alert">
                <p>Anzeige mit allen Fotos und Fragen endgültig löschen?</p>
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(false)}>
                  Abbrechen
                </button>
                <button className="btn btn-danger btn-sm" onClick={() => remove.mutate()}>
                  Löschen
                </button>
              </div>
            )}
          </div>
        )}
        <dl className="facts">
          <div>
            <dt>Verkauft von</dt>
            <dd>
              {a.seller?.id ? <Link to={`/mitglieder/${a.seller.id}`}>{a.seller.name}</Link> : (a.seller?.name ?? '–')}
            </dd>
          </div>
          <div>
            <dt>Eingestellt</dt>
            <dd>
              {dNum(new Date(a.created_at))} · {ago(new Date(a.created_at))}
            </dd>
          </div>
        </dl>
        {a.description && (
          <section>
            <h2 className="sec-h">Beschreibung</h2>
            <div className="desc" dangerouslySetInnerHTML={{ __html: md(a.description) }} />
          </section>
        )}
        <section>
          <h2 className="sec-h">
            {mineAd ? 'Fragen zu deiner Anzeige' : `Fragen an ${sellerFirst}`} <span>{a.questions.length}</span>
          </h2>
          {a.questions.length > 0 && (
            <ul className="comments">
              {a.questions.map(q => (
                <li key={q.id}>
                  <Avatar person={q.user} />
                  <div>
                    <b>{q.user.name}</b>
                    {q.is_seller && (
                      <span className="st st-new" style={{ marginLeft: 6 }}>
                        Verkäufer
                      </span>
                    )}{' '}
                    <small>{ago(new Date(q.created_at))}</small>
                    {q.can_delete && (
                      <>
                        {' '}
                        <button className="linkbtn" style={{ fontSize: 12 }} onClick={() => delQuestion.mutate(q.id)}>
                          löschen
                        </button>
                      </>
                    )}
                    <p>{q.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {(mineAd || sellerActive) && (
            <form
              className="cform"
              onSubmit={e => {
                e.preventDefault();
                if (question.trim()) ask.mutate();
              }}
            >
              <input
                value={question}
                onChange={e => setQuestion(e.target.value)}
                maxLength={1000}
                autoComplete="off"
                placeholder={mineAd ? 'Antwort schreiben …' : 'Noch zu haben? Anprobieren möglich? …'}
                aria-label={mineAd ? 'Antwort schreiben' : 'Frage schreiben'}
              />
              <button className="btn btn-primary btn-sm" disabled={ask.isPending || !question.trim()}>
                Senden
              </button>
            </form>
          )}
          <p className="note">
            {mineAd
              ? 'Alle, die gefragt haben, bekommen deine Antwort per E-Mail.'
              : sellerActive
                ? `${sellerFirst} bekommt deine Frage per E-Mail. Fragen und Antworten sehen alle Mitglieder.`
                : 'Die Person, die diese Anzeige aufgegeben hat, ist nicht mehr im Verein – Fragen sind nicht mehr möglich.'}
          </p>
        </section>
      </div>
    </article>
  );
}
